/**
 * Deep-link query params that identify a candidate/job pair (transcript drawer).
 * Invalid or HTML-encoded values used to leave the drawer spinning forever.
 */
const SELECTION_ID_FAMILIES = [
  ['selectedCandidateId', 'selectedCampaignId'],
  ['candidateId', 'campaignId'],
] as const;

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isEntityUuid = (
  value: string | null | undefined
): value is string => typeof value === 'string' && UUID_PATTERN.test(value);

/**
 * IDs interpolated into `/candidate/{id}/call_details/{id}` must not contain
 * query/path delimiters. A truncated or concatenated share link (UUID|url?…)
 * would otherwise produce a request that never resolves cleanly.
 */
export const isMalformedEntityId = (value: string): boolean =>
  value.length > 80 || /[|/?&#\s]/.test(value);

export enum SelectionSearchStatus {
  OK = 'ok',
  REWRITE = 'rewrite',
  MALFORMED = 'malformed',
}

export type SelectionSearchInspection =
  | { status: SelectionSearchStatus.OK; params: URLSearchParams }
  | { status: SelectionSearchStatus.REWRITE; params: URLSearchParams }
  | { status: SelectionSearchStatus.MALFORMED; cleanedParams: URLSearchParams };

const hasHtmlEncodedQuery = (search: string): boolean =>
  /&amp;|&lt;|&gt;|&#/.test(search);

const hasAmpPrefixedKeys = (params: URLSearchParams): boolean =>
  [...params.keys()].some((key) => key.startsWith('amp;'));

const canonicalizeAmpPrefixedKeys = (
  params: URLSearchParams
): URLSearchParams => {
  const next = new URLSearchParams();
  for (const [key, value] of params.entries()) {
    const canonical = key.startsWith('amp;') ? key.slice(4) : key;
    next.set(canonical, value);
  }
  return next;
};

const isInvalidEntityId = (value: string): boolean => !isEntityUuid(value);

const familyHasInvalidId = (
  params: URLSearchParams,
  family: readonly [string, string]
): boolean =>
  family.some((key) => {
    if (!params.has(key)) {
      return false;
    }
    return isInvalidEntityId(params.get(key) ?? '');
  });

const hasInvalidSelectionId = (params: URLSearchParams): boolean =>
  SELECTION_ID_FAMILIES.some((family) => familyHasInvalidId(params, family));

/**
 * True when `candidateId` and `campaignId` are not both present. Job-table
 * links keep the job id in the path, so callers must opt into this check.
 */
export const isIncompleteCandidateJobPair = (
  params: URLSearchParams
): boolean => !!params.get('candidateId') !== !!params.get('campaignId');

export const stripCandidateJobQueryParams = (
  params: URLSearchParams
): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete('candidateId');
  next.delete('campaignId');
  return next;
};

/** Strips a one-sided candidate/job query pair. Returns false when the caller should not open a drawer. */
export const applyIncompleteCandidateJobPair = (
  params: URLSearchParams,
  currentQuery: string,
  setSearchParams: (next: URLSearchParams, opts: { replace: true }) => void
): boolean => {
  if (!isIncompleteCandidateJobPair(params)) {
    return true;
  }
  const cleaned = stripCandidateJobQueryParams(params);
  if (cleaned.toString() !== currentQuery) {
    setSearchParams(cleaned, { replace: true });
  }
  return false;
};

export const stripSelectionParams = (
  params: URLSearchParams
): URLSearchParams => {
  const next = canonicalizeAmpPrefixedKeys(params);
  for (const family of SELECTION_ID_FAMILIES) {
    if (familyHasInvalidId(next, family)) {
      for (const key of family) {
        next.delete(key);
      }
    }
  }
  return next;
};

/**
 * Apply rewrite/strip from {@link inspectSelectionSearch}. Returns false when
 * the caller should not open a transcript drawer this pass (URL is changing or
 * the link was rejected).
 */
export const applySelectionSearchInspection = (
  inspection: SelectionSearchInspection,
  currentQuery: string,
  setSearchParams: (params: URLSearchParams, opts: { replace: true }) => void
): boolean => {
  if (inspection.status === SelectionSearchStatus.REWRITE) {
    if (inspection.params.toString() !== currentQuery) {
      setSearchParams(inspection.params, { replace: true });
    }
    return false;
  }
  if (inspection.status === SelectionSearchStatus.MALFORMED) {
    if (inspection.cleanedParams.toString() !== currentQuery) {
      setSearchParams(inspection.cleanedParams, { replace: true });
    }
    return false;
  }
  return true;
};

/**
 * Inspect a page search string for HTML-encoded `&` (`&amp;`) and non-UUID
 * candidate/job ids. Callers should rewrite or strip before opening a drawer.
 */
export const inspectSelectionSearch = (
  search: string
): SelectionSearchInspection => {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  const htmlEncoded = hasHtmlEncodedQuery(search);
  const decoded = htmlEncoded ? raw.replaceAll('&amp;', '&') : raw;
  const originalParams = new URLSearchParams(raw);
  const decodedParams = canonicalizeAmpPrefixedKeys(
    new URLSearchParams(decoded)
  );

  if (htmlEncoded || hasAmpPrefixedKeys(originalParams)) {
    if (
      !hasInvalidSelectionId(decodedParams) &&
      decodedParams.toString() !== originalParams.toString()
    ) {
      return { status: SelectionSearchStatus.REWRITE, params: decodedParams };
    }
    return {
      status: SelectionSearchStatus.MALFORMED,
      cleanedParams: stripSelectionParams(decodedParams),
    };
  }

  if (hasInvalidSelectionId(originalParams)) {
    return {
      status: SelectionSearchStatus.MALFORMED,
      cleanedParams: stripSelectionParams(originalParams),
    };
  }

  return { status: SelectionSearchStatus.OK, params: originalParams };
};
